import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Package, Megaphone, Users, GitBranch, UserPlus,
  BarChart3, LogOut, CalendarClock, Bot, Network,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark, Spinner } from "@/components/app/widgets";

const NAV = [
  { to: "/app", end: true, label: "Overview", icon: LayoutDashboard },
  { to: "/app/products", label: "Products", icon: Package },
  { to: "/app/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/app/engines", label: "Engines", icon: Network },
  { to: "/app/prospects", label: "Prospects", icon: Users },
  { to: "/app/pipeline", label: "Pipeline", icon: GitBranch },
  { to: "/app/follow-ups", label: "Follow-Ups", icon: CalendarClock },
  { to: "/app/leads", label: "Leads", icon: UserPlus },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/app/assistant", label: "AI Assistant", icon: Bot },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null) return <Spinner label="Preparing your workspace" />;
  if (user === false) {
    navigate("/login", { replace: true });
    return null;
  }

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-200 ${
      isActive
        ? "bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]"
        : "text-zinc-400 hover:text-white border-l-2 border-transparent"
    }`;

  return (
    <div data-testid="app-layout" className="flex min-h-screen bg-[#050505] text-white">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-white/10 bg-[#0A0A0A] lg:flex">
        <div className="border-b border-white/10 px-5 py-5">
          <BrandMark />
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto py-4">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`side-nav-${n.label.toLowerCase()}`} className={linkCls}>
              <n.icon className="h-4 w-4" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="mb-3 px-1">
            <p className="truncate text-sm font-semibold">{user.name}</p>
            <p className="truncate text-xs text-zinc-500">{user.email}</p>
            {!user.email_verified && (
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-amber-400">
                Email not verified
              </p>
            )}
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 transition-colors hover:text-red-400"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col lg:pl-60">
        <div className="sticky top-0 z-30 flex items-center gap-2 overflow-x-auto border-b border-white/10 bg-black/70 px-4 py-3 backdrop-blur-xl lg:hidden">
          <BrandMark small />
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${
                  isActive ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "text-zinc-400"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <button onClick={doLogout} className="ml-auto text-xs text-zinc-500">Sign out</button>
        </div>
        <main className="flex-1 px-4 py-8 sm:px-8 lg:px-10">
          <Outlet />
        </main>
        <footer className="border-t border-white/5 px-8 py-4 text-[11px] text-zinc-600">
          AI-assisted, human-approved.{" "}
          <Link to="/" className="hover:text-[#D4AF37]">Back to site</Link>
        </footer>
      </div>
    </div>
  );
}
